/* Stride offline store: queued daily entries, local photos and read-only cache (IndexedDB). */
const DB_NAME = "stride-offline";
const DB_VERSION = 3;
const QUEUE = "queue";
const CACHE = "cache";
const PHOTOS = "photos";
const GEN = "gen";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE, { keyPath: "key" });
      if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE, { keyPath: "key" });
      if (!db.objectStoreNames.contains(PHOTOS)) db.createObjectStore(PHOTOS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(GEN)) db.createObjectStore(GEN, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(store, mode, run) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = run(t.objectStore(store));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
  });
}

/* ---------- read-only cache ---------- */
export const cacheSet = (key, value) =>
  tx(CACHE, "readwrite", (s) => s.put({ key, value, at: new Date().toISOString() })).catch(() => null);

export const cacheGet = (key) => tx(CACHE, "readonly", (s) => s.get(key)).catch(() => null);

/* ---------- local photos ---------- */
export async function savePhoto(blob, caption = "") {
  const id = `ph-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await tx(PHOTOS, "readwrite", (s) => s.put({ id, blob, caption, at: new Date().toISOString() }));
  return id;
}

export const getPhoto = (id) => tx(PHOTOS, "readonly", (s) => s.get(id)).catch(() => null);
export const setPhotoCaption = async (id, caption) => {
  const rec = await getPhoto(id);
  if (rec) await tx(PHOTOS, "readwrite", (s) => s.put({ ...rec, caption })).catch(() => null);
};
export const deletePhoto = (id) => tx(PHOTOS, "readwrite", (s) => s.delete(id)).catch(() => null);

/* ---------- entry queue ---------- */
export const queueEntry = (payload) =>
  tx(QUEUE, "readwrite", (s) => s.put({
    key: `${payload.date}:${payload.volet}`, payload, at: new Date().toISOString(),
  }));

export const listPending = () => tx(QUEUE, "readonly", (s) => s.getAll()).then((r) => r || []).catch(() => []);
export const pendingCount = () => listPending().then((r) => r.length);
export const removePending = (key) => tx(QUEUE, "readwrite", (s) => s.delete(key)).catch(() => null);
export const getPending = (date, volet) =>
  tx(QUEUE, "readonly", (s) => s.get(`${date}:${volet}`)).catch(() => null);

/** Uploads the photos held on the device, then returns server-side paths. */
export async function uploadLocalPhotos(api, localIds) {
  const out = [];
  for (const id of localIds || []) {
    const rec = await getPhoto(id);
    if (!rec?.blob) continue;
    const fd = new FormData();
    fd.append("file", rec.blob, `${id}.jpg`);
    const res = await api.post("/harmonized/entry/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    out.push({ path: res.data.path, caption: rec.caption || "" });
    await deletePhoto(id);
  }
  return out;
}

/* ---------- offline-generated reports ---------- */
export const queueReport = (report) =>
  tx(GEN, "readwrite", (s) => s.put({ key: report.id, report, at: new Date().toISOString() }));
export const listLocalReports = () => tx(GEN, "readonly", (s) => s.getAll()).then((r) => r || []).catch(() => []);
export const getLocalReport = (id) => tx(GEN, "readonly", (s) => s.get(id)).catch(() => null);
export const removeLocalReport = (id) => tx(GEN, "readwrite", (s) => s.delete(id)).catch(() => null);
export const localReportsCount = () => listLocalReports().then((r) => r.length);

/** Sends offline-generated reports to the server once the entries are synced. */
export async function syncLocalReports(api) {
  const items = await listLocalReports();
  let sent = 0;
  const ids = [];
  for (const it of items) {
    try {
      const r = it.report;
      const res = await api.post("/harmonized/generate", r.body);
      if (r.edited) await api.put(`/harmonized/reports/${res.data.id}`, { narratives: r.narratives });
      await removeLocalReport(it.key);
      ids.push(res.data.id);
      sent += 1;
    } catch (err) {
      if (!err.response) break;
      await removeLocalReport(it.key);
    }
  }
  return { sent, ids };
}

/** Pushes every queued entry (and its local photos) to the server. */
export async function syncPending(api) {
  const items = await listPending();
  let sent = 0;
  let failed = 0;
  for (const it of items) {
    try {
      const { local_photos: locals, ...payload } = it.payload;
      const uploaded = await uploadLocalPhotos(api, locals);
      await api.post("/harmonized/entry", { ...payload, photos: [...(payload.photos || []), ...uploaded] });
      await removePending(it.key);
      sent += 1;
    } catch (err) {
      if (!err.response) { failed += 1; break; }  // still offline: keep the queue intact
      failed += 1;
      await removePending(it.key);                // rejected by the server: drop to avoid a stuck queue
    }
  }
  const reports = failed === 0 ? await syncLocalReports(api) : { sent: 0, ids: [] };
  window.dispatchEvent(new CustomEvent("stride-queue-changed"));
  return { sent, failed, total: items.length, reports: reports.sent, report_ids: reports.ids };
}

export const notifyQueueChanged = () => window.dispatchEvent(new CustomEvent("stride-queue-changed"));

export const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

/** Downscales and compresses a picked image to keep mobile data usage low. */
export function compressImage(file, maxSide = 1600, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("compression failed"))), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image illisible")); };
    img.src = url;
  });
}
